import React, { useState, useEffect, useRef } from "react";
import { Container, Card, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Lock,
  GraduationCap,
  Contact,
  Handshake,
  Globe,
  UserCheck,
  LayoutGrid,
  CalendarDays,
  MapPin,
  X,
} from "lucide-react";

import Navbar from "./NavbarHome";
import { ContattiIcon } from "../icone-utility/IcoCont";
import BackOrderIcon from "../icone-utility/IcoBO";
import TicketIcon from "../icone-utility/IcoTicket";
import TicketRitiri from "../icone-utility/IcoRitiri";

import { useAuth } from "../../auth/AuthContext";

import technopartsLogo from "../../components/images/TP-_logo-symbol-white.png";
import amotivelogo from "../../components/images/amotive-logo-symbol-inverted-cut.png";
import technopartsLogoecommerce from "../../components/images/TP-_logo-symbol-color.png";
import amotivelogoecommerce from "../../components/images/amotive-logo-symbol-color.png";

import crm from "../../components/images/crm.png";
import people from "../../components/images/people.png";
import learn from "../../components/images/learn.png";
import vault from "../../components/images/vault.png";

const RECENT_APPS_LIMIT = 6;
const RECENT_APPS_PREFIX = "uw_recent_apps_";

// ======================================================
// THEME
// ======================================================

const GOLD = "#ebb51b";

const GLASS_BG = "rgba(255,255,255,0.075)";
const GLASS_BG_STRONG = "rgba(255,255,255,0.105)";
const GLASS_BORDER = "rgba(255,255,255,0.16)";

const TEXT = "#f7f7f8";
const TEXT_MUTED = "rgba(247,247,248,0.62)";

// ======================================================
// COMPONENT
// ======================================================

function Home() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  const [activeSection, setActiveSection] = useState("utility");

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);

  const [recentApps, setRecentApps] = useState([]);

  const [openGroupId, setOpenGroupId] = useState(null);
  const [closingGroupId, setClosingGroupId] = useState(null);
  const [originRect, setOriginRect] = useState(null);

  const tileRefs = useRef({});
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});

  // ======================================================
  // CALENDARIO
  // ======================================================

  useEffect(() => {
    const loadCalendar = async () => {
      try {
        setCalendarLoading(true);
        setCalendarError(null);

        const response = await fetch(
          "https://dashboard.technoparts.it/ws/calendar/calendar.php",
        );

        if (!response.ok) {
          throw new Error(`Errore HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Errore caricamento calendario");
        }

        setCalendarEvents(Array.isArray(data.events) ? data.events : []);
      } catch (error) {
        console.error("Errore calendario:", error);
        setCalendarError(error.message);
      } finally {
        setCalendarLoading(false);
      }
    };

    loadCalendar();
  }, []);

  // ======================================================
  // SERVIZI
  // ======================================================

  const dashboardService = {
    title: "Dashboard",
    desc: "Cruscotti operativi e reportistica in tempo reale su ordini, KPI e performance.",
    btnText: "Apri Dashboard",
    route: "/technoparts",
  };

  const zohoServices = [
    {
      title: "Zoho CRM",
      desc: "Gestione clienti, opportunità e attività commerciali.",
      btnText: "Vai al CRM",
      icon: Handshake,
      image: crm,
      route: "https://crm.zoho.com",
    },
    {
      title: "Zoho Vault",
      desc: "Gestione sicura delle password e delle credenziali.",
      btnText: "Apri Vault",
      icon: Lock,
      image: vault,
      route: "https://vault.zoho.com",
    },
    {
      title: "Zoho People",
      desc: "Gestione delle informazioni e dei processi HR.",
      btnText: "Apri People",
      icon: UserCheck,
      image: people,
      route: "https://people.zoho.com",
    },
    {
      title: "Zoho Learn",
      desc: "Formazione, documentazione e conoscenza aziendale.",
      btnText: "Vai a Learn",
      image: learn,
      icon: GraduationCap,
      route: "https://learn.zoho.com",
    },
  ];

  const webServices = [
    {
      title: "technoparts.it",
      desc: "Sito web ufficiale Technoparts.",
      btnText: "Visita il sito",
      icon: Globe,
      image: technopartsLogo,
      route: "https://www.technoparts.it",
    },
    {
      title: "amotive.it",
      desc: "Sito web ufficiale Amotive.",
      btnText: "Visita il sito",
      icon: Globe,
      image: amotivelogo,
      route: "https://www.amotive.it",
    },
    {
      title: "Store TP",
      desc: "E-Commerce Technoparts, prodotti e catalogo.",
      btnText: "Vai allo store",
      icon: ShoppingCart,
      image: technopartsLogoecommerce,
      route: "https://storetp.technoparts.it/",
    },
    {
      title: "Store Amotive",
      desc: "E-Commerce Amotive, prodotti e ricambi.",
      btnText: "Vai allo store",
      icon: ShoppingCart,
      image: amotivelogoecommerce,
      route: "https://store.amotive.it/",
    },
    {
      title: "Store TP",
      desc: "E-Commerce Technoparts, prodotti e catalogo.",
      btnText: "Vai allo store",
      icon: ShoppingCart,
      image: technopartsLogoecommerce,
      route: "https://storetp.technoparts.it/",
    },
    {
      title: "Store Amotive",
      desc: "E-Commerce Amotive, prodotti e ricambi.",
      btnText: "Vai allo store",
      icon: ShoppingCart,
      image: amotivelogoecommerce,
      route: "https://store.amotive.it/",
    },
  ];

  const utilityServices = [
    {
      title: "Contatti",
      desc: "Rubrica e contatti aziendali.",
      icon: ContattiIcon,
      route: "/utility/add-contatti",
    },
    {
      title: "Back Order",
      desc: "Stato e gestione degli ordini in arretrato.",
      icon: BackOrderIcon,
      route: "/utility/backorder",
    },
  ];
  const technopartsServices = [
    {
      title: "Ticket",
      desc: "Gestione dei ticket di supporto.",
      icon: TicketIcon,
      route: "/utility/ticket",
    },
    
  ];
  const fornitori = [
    {
      title: "Ritiri",
      desc: "Gestione dei ritiri e delle consegne.",
      icon: TicketRitiri,
      route: "/utility/ritiri",
    },
    
  ];

  const macroGroups = [
    {
      id: "zoho",
      title: "Zoho",
      subtitle: "Ecosistema Zoho",
      icon: LayoutGrid,
      items: zohoServices,
    },
    {
      id: "web",
      title: "Siti Web",
      subtitle: "Siti & E-Commerce",
      icon: Globe,
      items: webServices,
    },
    {
      id: "clienti",
      title: "CRM",
      subtitle: "Utility di lavoro",
      icon: Contact,
      items: utilityServices,
    },
    {
      id: "technoparts",
      title: "Technoparts",
      subtitle: "Servizi Technoparts",
      icon: Contact,
      items: technopartsServices,
    },
    {
      id: "fornitori",
      title: "Fornitori",
      subtitle: "Servizi Fornitori",
      icon: Contact,
      items: fornitori,
    },
  ];

  // ======================================================
  // RECENTI
  // ======================================================

  const allApps = [
    dashboardService,
    ...zohoServices,
    ...webServices,
    ...utilityServices,
    ...technopartsServices,

  ];

  const recentUserKey =
    user?.id ?? user?.username ?? user?.email ?? user ?? "guest";

  const recentStorageKey = `${RECENT_APPS_PREFIX}${recentUserKey}`;

  useEffect(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(recentStorageKey));

      setRecentApps(Array.isArray(stored) ? stored : []);
    } catch {
      setRecentApps([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const recordRecentApp = (item) => {
    if (!item || !item.route || item.route === "#") {
      return;
    }

    setRecentApps((prev) => {
      const filtered = prev.filter((app) => app.route !== item.route);

      const updated = [
        {
          title: item.title,
          route: item.route,
        },
        ...filtered,
      ].slice(0, RECENT_APPS_LIMIT);

      try {
        sessionStorage.setItem(recentStorageKey, JSON.stringify(updated));
      } catch (error) {
        console.error("Errore salvataggio app recenti:", error);
      }

      return updated;
    });
  };

  // ======================================================
  // INTERSECTION OBSERVER
  // ======================================================

  useEffect(() => {
    const root = scrollRef.current;

    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.dataset.section);
          }
        });
      },
      {
        root,
        threshold: 0.35,
      },
    );

    Object.values(sectionRefs.current).forEach((element) => {
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  // ======================================================
  // NAVIGAZIONE
  // ======================================================

  const handleNavigation = (route, item) => {
    if (!route || route === "#") return;

    if (item) {
      recordRecentApp(item);
    }

    if (route.startsWith("http://") || route.startsWith("https://")) {
      window.open(route, "_blank", "noopener,noreferrer");
    } else {
      navigate(route);
    }
  };

  // ======================================================
  // FOLDER
  // ======================================================

  const openFolder = (groupId) => {
    const element = tileRefs.current[groupId];

    if (element) {
      const rect = element.getBoundingClientRect();
      setOriginRect(rect);
    }

    setOpenGroupId(groupId);
  };

  const closeFolder = () => {
    if (!openGroupId) return;

    setClosingGroupId(openGroupId);
    setOpenGroupId(null);

    window.setTimeout(() => {
      setClosingGroupId(null);
      setOriginRect(null);
    }, 280);
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeFolder();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openGroupId]);

  const activeGroupId = openGroupId || closingGroupId;

  const activeGroup =
    macroGroups.find((group) => group.id === activeGroupId) || null;

  const isClosing = !!closingGroupId && !openGroupId;

  // ======================================================
  // SECTION LABEL
  // ======================================================

  const SectionLabel = ({ children }) => (
    <div className="uw-section-label">
      <span className="uw-section-line" />
      <span>{children}</span>
    </div>
  );

  // ======================================================
  // OUTLINE BUTTON
  // ======================================================

  const OutlineButton = ({ children, onClick }) => (
    <Button variant="light" className="uw-outline-btn" onClick={onClick}>
      {children}
    </Button>
  );

  // ======================================================
  // SERVICE CARD
  // ======================================================

  const ServiceCard = ({ service }) => {
    const Icon = service.icon;

    return (
      <Card className="uw-service-card">
        <div className="uw-service-card-inner">
          <div className="uw-service-icon">
            {service.image ? (
              <img src={service.image} alt={service.title} />
            ) : Icon ? (
              <Icon size={28} />
            ) : null}
          </div>

          <div className="uw-service-content">
            <h5>{service.title}</h5>
            <p>{service.desc}</p>

            <OutlineButton
              onClick={() => handleNavigation(service.route, service)}
            >
              {service.btnText}
            </OutlineButton>
          </div>
        </div>
      </Card>
    );
  };

  // ======================================================
  // FOLDER TILE
  // ======================================================

  const FolderTile = ({ group }) => {
    const hasMore = group.items.length > 4;

    const handleTileClick = () => {
      if (hasMore) {
        openFolder(group.id);
      }
    };

    return (
      <div
        ref={(element) => {
          tileRefs.current[group.id] = element;
        }}
        className={`uw-folder-tile ${
          hasMore ? "uw-folder-tile-clickable" : ""
        }`}
        onClick={handleTileClick}
      >
        <div className="uw-folder-mini-grid">
          {group.items.slice(0, 4).map((item, index) => {
            const Icon = item.icon;

            return (
              <button
                key={`${item.title}-${index}`}
                className="uw-folder-mini-icon"
                onClick={(event) => {
                  event.stopPropagation();

                  // Come nella Libreria App di Apple:
                  // con più di 4 app la preview apre la cartella;
                  // con 4 app o meno apre direttamente l'app.
                  if (hasMore) {
                    openFolder(group.id);
                  } else {
                    handleNavigation(item.route, item);
                  }
                }}
                title={hasMore ? `Apri ${group.title}` : item.title}
                aria-label={
                  hasMore
                    ? `Apri cartella ${group.title}`
                    : `Apri ${item.title}`
                }
              >
                <span className="uw-folder-mini-artwork">
                  {item.image ? (
                    <img src={item.image} alt="" />
                  ) : Icon ? (
                    <Icon size={24} />
                  ) : null}
                </span>

                <span className="uw-folder-mini-name">{item.title}</span>
              </button>
            );
          })}
        </div>

        <div className="uw-folder-info">
          <div className="uw-folder-title">{group.title}</div>

          {hasMore && (
            <div className="uw-folder-count">{group.items.length} app</div>
          )}
        </div>
      </div>
    );
  };

  // ======================================================
  // FOLDER APP TILE
  // ======================================================

  const FolderAppTile = ({ item }) => {
    const Icon = item.icon;

    return (
      <button
        className="uw-folder-app"
        onClick={() => handleNavigation(item.route, item)}
        title={item.title}
        aria-label={`Apri ${item.title}`}
      >
        <div className="uw-folder-app-icon">
          <span className="uw-folder-app-artwork">
            {item.image ? (
              <img src={item.image} alt="" />
            ) : Icon ? (
              <Icon size={31} />
            ) : null}
          </span>

          <span className="uw-folder-app-title">{item.title}</span>
        </div>
      </button>
    );
  };

  // ======================================================
  // MARQUEE
  // ======================================================

  const MarqueeText = ({ children }) => {
    const title = typeof children === "string" ? children : undefined;

    return (
      <div className="uw-marquee" title={title}>
        <span>{children}</span>
      </div>
    );
  };

  // ======================================================
  // CALENDARIO
  // ======================================================

  const parseCalendarDate = (value) => {
    if (!value) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const cleanCalendarText = (value) => {
    if (!value) return "";

    const raw = String(value);
    const normalizeBreaks = (text) =>
      text.replace(/<br\s*\/?\s*>/gi, " · ");

    if (typeof DOMParser === "undefined") {
      return normalizeBreaks(raw)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    const parser = new DOMParser();
    const decode = (text) =>
      parser.parseFromString(normalizeBreaks(text), "text/html").body
        .textContent || "";

    // Prima passata: rimuove l'HTML normale e decodifica le entity.
    let cleaned = decode(raw);

    // Seconda passata solo se l'API ha restituito HTML già escapato
    // (es. &lt;a href=...&gt;).
    if (/<[a-z][\s\S]*>/i.test(cleaned)) {
      cleaned = decode(cleaned);
    }

    return cleaned
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const CalendarSidebar = () => {
    const groupedEvents = {};

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sixMonthsFromToday = new Date(today);
    sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);

    calendarEvents.forEach((event) => {
      const startValue =
        event.inizio || event.date || event.start || event.start_date;

      const endValue =
        event.fine || event.end || event.end_date || startValue;

      const startDate = parseCalendarDate(startValue);
      const endDate = parseCalendarDate(endValue) || startDate;

      if (!startDate || !endDate) return;

      // Mostra solo eventi che intersecano la finestra da oggi a +6 mesi.
      if (endDate < today || startDate > sixMonthsFromToday) return;

      // Se un evento è già iniziato ma è ancora in corso, lo mostriamo sotto oggi.
      const displayDate = startDate < today ? today : startDate;

      const monthKey =
        displayDate.getFullYear() +
        "-" +
        String(displayDate.getMonth() + 1).padStart(2, "0");

      const dayKey = displayDate.getDate();

      if (!groupedEvents[monthKey]) {
        groupedEvents[monthKey] = {};
      }

      if (!groupedEvents[monthKey][dayKey]) {
        groupedEvents[monthKey][dayKey] = [];
      }

      groupedEvents[monthKey][dayKey].push(event);
    });

    const monthEntries = Object.entries(groupedEvents).sort(
      ([a], [b]) => new Date(a + "-01") - new Date(b + "-01"),
    );

    return (
      <aside className="uw-calendar">
        <div className="uw-calendar-header">
          <div className="uw-calendar-header-icon">
            <CalendarDays size={19} />
          </div>

          <div>
            <div className="uw-calendar-title">Calendario</div>

            <div className="uw-calendar-subtitle">Da oggi · prossimi 6 mesi</div>
          </div>
        </div>

        <div className="uw-calendar-body">
          {calendarLoading && (
            <div className="uw-calendar-empty">Caricamento calendario...</div>
          )}

          {!calendarLoading && calendarError && (
            <div className="uw-calendar-error">{calendarError}</div>
          )}

          {!calendarLoading && !calendarError && monthEntries.length === 0 && (
            <div className="uw-calendar-empty">Nessun evento disponibile.</div>
          )}

          {!calendarLoading &&
            !calendarError &&
            monthEntries.map(([monthKey, days]) => {
              const monthDate = new Date(monthKey + "-01");

              const monthName = monthDate.toLocaleDateString("it-IT", {
                month: "long",
                year: "numeric",
              });

              return (
                <div className="uw-calendar-month" key={monthKey}>
                  <div className="uw-calendar-month-title">{monthName}</div>

                  {Object.entries(days)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([day, events]) => {
                      const dayDate = new Date(
                        monthDate.getFullYear(),
                        monthDate.getMonth(),
                        Number(day),
                      );

                      const weekday = dayDate.toLocaleDateString("it-IT", {
                        weekday: "short",
                      });

                      return (
                        <div
                          className="uw-calendar-day"
                          key={`${monthKey}-${day}`}
                        >
                          <div className="uw-calendar-day-head">
                            <div className="uw-calendar-day-number">{day}</div>

                            <div className="uw-calendar-day-name">
                              {weekday}
                            </div>
                          </div>

                          <div className="uw-calendar-events">
                            {events.map((event, index) => {
                              const title = cleanCalendarText(
                                event.titolo ||
                                  event.title ||
                                  event.name ||
                                  event.summary ||
                                  "Evento",
                              );

                              const description = cleanCalendarText(
                                event.descrizione ||
                                  event.description ||
                                  event.desc ||
                                  "",
                              );

                              const location = cleanCalendarText(
                                event.luogo ||
                                  event.location ||
                                  event.place ||
                                  "",
                              );

                              const isLeave = /ferie|permess/i.test(
                                `${title} ${description}`,
                              );

                              return (
                                <div
                                  key={`${title}-${index}`}
                                  className={`uw-calendar-event ${
                                    isLeave ? "is-leave" : ""
                                  }`}
                                >
                                  <div className="uw-calendar-event-dot" />

                                  <div className="uw-calendar-event-content">
                                    <MarqueeText>{title}</MarqueeText>

                                    {description && (
                                      <div className="uw-calendar-event-description">
                                        {description}
                                      </div>
                                    )}

                                    {location && (
                                      <div className="uw-calendar-event-location">
                                        <MapPin size={12} />
                                        {location}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}
        </div>
      </aside>
    );
  };

  // ======================================================
  // RENDER
  // ======================================================

  return (
    <div className="uw-home-shell">
      <div className="uw-ambient uw-ambient-one" />
      <div className="uw-ambient uw-ambient-two" />
      <div className="uw-ambient uw-ambient-three" />

      <div ref={scrollRef} className="uw-home-scroll">
        <Navbar />

        <Container fluid className="uw-home-container">
          <div className="uw-main-layout">
            <main className="uw-main-content">
              {/* ==================================================
                  INTRO
              ================================================== */}

              <section
                className="uw-intro"
                ref={(element) => {
                  sectionRefs.current.intro = element;
                }}
                data-section="intro"
              >
                <div className="uw-intro-badge">
                  <span />
                  Utility Web · Anno 2026
                </div>

                <h1>
                  Pannello di <span>Controllo</span>
                </h1>

                <p>
                  Tutti gli strumenti aziendali, i servizi web e le utility di
                  lavoro in un unico spazio.
                </p>
              </section>

              {/* ==================================================
                  PANORAMICA
              ================================================== */}

              <section
                className="uw-section"
                ref={(element) => {
                  sectionRefs.current.panoramica = element;
                }}
                data-section="panoramica"
              >
                <SectionLabel>Panoramica</SectionLabel>

                <Card className="uw-dashboard-card">
                  <div className="uw-dashboard-glow" />

                  <div className="uw-dashboard-content">
                    <div className="uw-dashboard-icon">
                      <LayoutGrid size={27} />
                    </div>

                    <div className="uw-dashboard-text">
                      <div className="uw-dashboard-eyebrow">Workspace</div>

                      <h2>{dashboardService.title}</h2>

                      <p>{dashboardService.desc}</p>
                    </div>

                    <OutlineButton
                      onClick={() =>
                        handleNavigation(
                          dashboardService.route,
                          dashboardService,
                        )
                      }
                    >
                      {dashboardService.btnText}
                    </OutlineButton>
                  </div>
                </Card>
              </section>

              {/* ==================================================
                  RECENTI
              ================================================== */}

              {recentApps.length > 0 && (
                <section
                  className="uw-section"
                  ref={(element) => {
                    sectionRefs.current.recenti = element;
                  }}
                  data-section="recenti"
                >
                  <SectionLabel>Recenti</SectionLabel>

                  <div className="uw-recent-row">
                    {recentApps.map((recent, index) => {
                      const original = allApps.find(
                        (app) => app.route === recent.route,
                      );

                      const Icon = original?.icon;

                      return (
                        <button
                          className="uw-recent-card"
                          key={`${recent.route}-${index}`}
                          onClick={() =>
                            handleNavigation(recent.route, original || recent)
                          }
                        >
                          <div className="uw-recent-icon">
                            {original?.image ? (
                              <img src={original.image} alt={recent.title} />
                            ) : Icon ? (
                              <Icon size={21} />
                            ) : (
                              <Globe size={21} />
                            )}
                          </div>

                          <span>{recent.title}</span>

                          <b>→</b>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* ==================================================
                  GRUPPI
              ================================================== */}

              <section
                className="uw-section uw-groups-section"
                ref={(element) => {
                  sectionRefs.current.gruppi = element;
                }}
                data-section="gruppi"
              >
                <SectionLabel>Applicazioni</SectionLabel>

                <div className="uw-folder-row">
                  {macroGroups.map((group) => (
                    <FolderTile key={group.id} group={group} />
                  ))}
                </div>
              </section>
            </main>

            {/* ==================================================
                SIDEBAR
            ================================================== */}

            <div className="uw-sidebar">
              <CalendarSidebar />
            </div>
          </div>
        </Container>
      </div>

      {/* ========================================================
          FOLDER OVERLAY
      ======================================================== */}

      {activeGroup && (
        <div
          className={`uw-folder-overlay ${
            isClosing ? "is-closing" : "is-opening"
          }`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeFolder();
            }
          }}
        >
          <div
            className="uw-folder-modal"
            style={
              originRect
                ? {
                    "--origin-x": `${originRect.left + originRect.width / 2}px`,
                    "--origin-y": `${originRect.top + originRect.height / 2}px`,
                  }
                : {}
            }
          >
            <div className="uw-folder-modal-shine" />

            <div className="uw-folder-modal-header">
              <div className="uw-folder-modal-heading">
                <div className="uw-folder-modal-icon">
                  {React.createElement(activeGroup.icon, {
                    size: 25,
                  })}
                </div>

                <div>
                  <div className="uw-folder-modal-kicker">Applicazioni</div>

                  <h2>{activeGroup.title}</h2>

                  <p>{activeGroup.subtitle}</p>
                </div>
              </div>

              <button
                className="uw-folder-close"
                onClick={closeFolder}
                aria-label="Chiudi"
              >
                <X size={21} />
              </button>
            </div>

            <div className="uw-folder-app-grid">
              {activeGroup.items.map((item, index) => (
                <FolderAppTile key={`${item.title}-${index}`} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================
          CSS
      ======================================================== */}

      <style>{`

        /* ======================================================
           GLOBAL
        ====================================================== */

        * {
          box-sizing: border-box;
        }

        .uw-home-shell {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          color: ${TEXT};
          background:
            radial-gradient(
              circle at 8% 8%,
              rgba(235,181,27,0.13),
              transparent 26%
            ),
            radial-gradient(
              circle at 88% 12%,
              rgba(255,255,255,0.075),
              transparent 25%
            ),
            radial-gradient(
              circle at 52% 100%,
              rgba(84,100,130,0.12),
              transparent 35%
            ),
            #202227;
        }

        .uw-home-shell::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,0.025),
              transparent 35%
            );
          z-index: 0;
        }

        .uw-home-scroll {
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color:
            rgba(255,255,255,0.18)
            transparent;
        }

        .uw-home-scroll::-webkit-scrollbar {
          width: 7px;
        }

        .uw-home-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .uw-home-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.18);
          border-radius: 99px;
        }

        /* ======================================================
           AMBIENT ORBS
        ====================================================== */

        .uw-ambient {
          position: absolute;
          pointer-events: none;
          border-radius: 50%;
          filter: blur(70px);
          opacity: 0.32;
          z-index: 0;
        }

        .uw-ambient-one {
          width: 280px;
          height: 280px;
          top: 18%;
          left: -100px;
          background: rgba(235,181,27,0.11);
        }

        .uw-ambient-two {
          width: 320px;
          height: 320px;
          right: -120px;
          top: 35%;
          background: rgba(110,130,170,0.09);
        }

        .uw-ambient-three {
          width: 240px;
          height: 240px;
          left: 45%;
          bottom: -140px;
          background: rgba(255,255,255,0.055);
        }

        /* ======================================================
           CONTAINER
        ====================================================== */

        .uw-home-container {
          padding:
            34px
            clamp(20px, 4vw, 60px)
            70px;
        }

        .uw-main-layout {
          display: grid;
          grid-template-columns:
            minmax(0, 1fr)
            350px;
          gap: 30px;
          max-width: 1700px;
          margin: 0 auto;
        }

        .uw-main-content {
          min-width: 0;
        }

        /* ======================================================
           INTRO
        ====================================================== */

        .uw-intro {
          padding:
            25px
            4px
            38px;
        }

        .uw-intro-badge {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 15px;
          color: rgba(247,247,248,0.48);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.8px;
          text-transform: uppercase;
        }

        .uw-intro-badge span {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: ${GOLD};
          box-shadow:
            0 0 12px rgba(235,181,27,0.65);
        }

        .uw-intro h1 {
          margin: 0;
          font-size:
            clamp(42px, 5vw, 70px);
          line-height: 0.98;
          font-weight: 300;
          letter-spacing: -3px;
          color: ${TEXT};
        }

        .uw-intro h1 span {
          color: ${GOLD};
          font-weight: 600;
          text-shadow:
            0 0 28px rgba(235,181,27,0.18);
        }

        .uw-intro p {
          max-width: 650px;
          margin:
            22px
            0
            0;
          color: ${TEXT_MUTED};
          font-size: 15px;
          line-height: 1.7;
        }

        /* ======================================================
           SECTION
        ====================================================== */

        .uw-section {
          margin-bottom: 34px;
        }

        .uw-section-label {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 13px;
          padding-left: 3px;
          color: rgba(247,247,248,0.48);
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
        }

        .uw-section-line {
          width: 24px;
          height: 2px;
          border-radius: 99px;
          background: ${GOLD};
          box-shadow:
            0 0 10px rgba(235,181,27,0.45);
        }

        /* ======================================================
           DASHBOARD GLASS
        ====================================================== */

        .uw-dashboard-card {
          position: relative;
          overflow: hidden;
          border: 1px solid
            rgba(255,255,255,0.15) !important;
          border-radius: 28px !important;
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,0.105),
              rgba(255,255,255,0.045)
            ) !important;
          box-shadow:
            0 25px 70px
              rgba(0,0,0,0.27),
            inset 0 1px 0
              rgba(255,255,255,0.16),
            inset 0 -1px 0
              rgba(0,0,0,0.08);
          backdrop-filter:
            blur(24px)
            saturate(135%);
          -webkit-backdrop-filter:
            blur(24px)
            saturate(135%);
        }

        .uw-dashboard-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 55%;
          height: 1px;
          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,0.42),
              transparent
            );
        }

        .uw-dashboard-glow {
          position: absolute;
          width: 250px;
          height: 250px;
          right: -80px;
          top: -100px;
          border-radius: 50%;
          background:
            rgba(235,181,27,0.08);
          filter: blur(45px);
          pointer-events: none;
        }

        .uw-dashboard-content {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 26px;
        }

        .uw-dashboard-icon {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: 20px;
          color: ${GOLD};
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.19),
              rgba(235,181,27,0.06)
            );
          border: 1px solid
            rgba(235,181,27,0.27);
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.12),
            0 10px 30px
              rgba(0,0,0,0.15);
        }

        .uw-dashboard-text {
          flex: 1;
          min-width: 0;
        }

        .uw-dashboard-eyebrow {
          margin-bottom: 3px;
          color: rgba(255,255,255,0.42);
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 1.7px;
          text-transform: uppercase;
        }

        .uw-dashboard-text h2 {
          margin: 0;
          color: ${TEXT};
          font-size: 24px;
          font-weight: 600;
          letter-spacing: -0.6px;
        }

        .uw-dashboard-text p {
          max-width: 650px;
          margin:
            5px
            0
            0;
          color: ${TEXT_MUTED};
          font-size: 13px;
          line-height: 1.55;
        }

        /* ======================================================
           BUTTON
        ====================================================== */

        .uw-outline-btn {
          flex-shrink: 0;
          border: 1px solid
            rgba(235,181,27,0.55) !important;
          border-radius: 999px !important;
          padding:
            9px
            17px !important;
          color: ${GOLD} !important;
          background:
            rgba(235,181,27,0.055) !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.10),
            0 8px 22px
              rgba(0,0,0,0.10);
          transition:
            all 0.22s ease;
        }

        .uw-outline-btn:hover {
          transform:
            translateY(-2px);
          border-color: ${GOLD} !important;
          color: #fff !important;
          background:
            rgba(235,181,27,0.13) !important;
          box-shadow:
            0 10px 28px
              rgba(0,0,0,0.20),
            0 0 22px
              rgba(235,181,27,0.10);
        }

        /* ======================================================
           RECENT
        ====================================================== */

        .uw-recent-row {
          display: grid;
          grid-template-columns:
            repeat(
              auto-fit,
              minmax(170px, 1fr)
            );
          gap: 10px;
        }

        .uw-recent-card {
          position: relative;
          display: flex;
          align-items: center;
          min-width: 0;
          gap: 10px;
          padding: 12px 13px;
          border: 1px solid
            rgba(255,255,255,0.12);
          border-radius: 17px;
          color: ${TEXT};
          background:
            linear-gradient(
              135deg,
              rgba(255,255,255,0.075),
              rgba(255,255,255,0.035)
            );
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.10);
          backdrop-filter:
            blur(18px);
          -webkit-backdrop-filter:
            blur(18px);
          cursor: pointer;
          text-align: left;
          transition:
            transform 0.22s ease,
            border-color 0.22s ease,
            background 0.22s ease;
        }

        .uw-recent-card:hover {
          transform:
            translateY(-3px);
          border-color:
            rgba(235,181,27,0.28);
          background:
            rgba(255,255,255,0.095);
        }

        .uw-recent-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          width: 38px;
          height: 38px;
          overflow: hidden;
          border-radius: 12px;
          color: ${GOLD};
          background:
            rgba(255,255,255,0.08);
          border: 1px solid
            rgba(255,255,255,0.10);
        }

        .uw-recent-icon img {
          width: 70%;
          height: 70%;
          object-fit: contain;
        }

        .uw-recent-card span {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          color:
            rgba(255,255,255,0.82);
          font-size: 11px;
          font-weight: 650;
        }

        .uw-recent-card b {
          margin-left: auto;
          color:
            rgba(255,255,255,0.35);
          font-size: 14px;
          transition:
            color 0.2s ease,
            transform 0.2s ease;
        }

        .uw-recent-card:hover b {
          color: ${GOLD};
          transform:
            translateX(2px);
        }

        /* ======================================================
           FOLDER ROW
        ====================================================== */

        .uw-folder-row {
          display: grid;
          grid-template-columns:
            repeat(
              3,
              minmax(0, 1fr)
            );
          gap: 18px;
        }

        /* ======================================================
           FOLDER TILE
        ====================================================== */

        .uw-folder-tile {
          position: relative;
          aspect-ratio: 1 / 0.88;
          min-height: 230px;
          overflow: hidden;
          padding: 19px;
          border: 1px solid
            rgba(255,255,255,0.14);
          border-radius: 27px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.11),
              rgba(255,255,255,0.045)
            );
          box-shadow:
            0 22px 55px
              rgba(0,0,0,0.22),
            inset 0 1px 0
              rgba(255,255,255,0.17),
            inset 0 -1px 0
              rgba(0,0,0,0.08);
          backdrop-filter:
            blur(25px)
            saturate(135%);
          -webkit-backdrop-filter:
            blur(25px)
            saturate(135%);
          cursor: pointer;
          transition:
            transform 0.28s ease,
            border-color 0.28s ease,
            box-shadow 0.28s ease;
        }

        .uw-folder-tile::before {
          content: "";
          position: absolute;
          top: 0;
          left: 12%;
          width: 55%;
          height: 1px;
          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,0.42),
              transparent
            );
        }

        .uw-folder-tile:hover {
          transform:
            translateY(-6px)
            scale(1.008);
          border-color:
            rgba(235,181,27,0.30);
          box-shadow:
            0 28px 70px
              rgba(0,0,0,0.30),
            0 0 35px
              rgba(235,181,27,0.055),
            inset 0 1px 0
              rgba(255,255,255,0.20);
        }

        .uw-folder-shine {
          position: absolute;
          width: 180px;
          height: 180px;
          top: -120px;
          right: -60px;
          border-radius: 50%;
          background:
            rgba(255,255,255,0.08);
          filter: blur(30px);
          pointer-events: none;
        }

        .uw-folder-top {
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .uw-folder-main-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 15px;
          color: ${GOLD};
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,0.17),
              rgba(235,181,27,0.045)
            );
          border: 1px solid
            rgba(235,181,27,0.23);
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.12);
        }

        .uw-folder-more {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 30px;
          height: 30px;
          padding: 0 8px;
          border-radius: 999px;
          color:
            rgba(255,255,255,0.60);
          background:
            rgba(255,255,255,0.07);
          border: 1px solid
            rgba(255,255,255,0.10);
          font-size: 10px;
          font-weight: 800;
        }

        .uw-folder-mini-grid {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns:
            repeat(2, minmax(0, 1fr));
          gap: 9px;
          width: 100%;
          margin-top: 17px;
        }

        .uw-folder-mini-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 62px;
          padding: 10px;
          border: 1px solid
            rgba(255,255,255,0.10);
          border-radius: 15px;
          color:
            rgba(255,255,255,0.76);
          background:
            rgba(255,255,255,0.052);
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.08);
          cursor: pointer;
          transition:
            all 0.2s ease;
        }

        .uw-folder-mini-icon:hover {
          transform:
            translateY(-2px);
          color: ${GOLD};
          border-color:
            rgba(235,181,27,0.30);
          background:
            rgba(235,181,27,0.075);
        }

        .uw-folder-mini-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .uw-folder-info {
          position: absolute;
          left: 19px;
          right: 19px;
          bottom: 17px;
          z-index: 3;
        }

        .uw-folder-title {
          color: ${TEXT};
          font-size: 16px;
          font-weight: 650;
          letter-spacing: -0.2px;
        }

        .uw-folder-subtitle {
          margin-top: 2px;
          color:
            rgba(255,255,255,0.43);
          font-size: 10px;
        }

        .uw-folder-open {
          position: absolute;
          right: 18px;
          bottom: 19px;
          display: flex;
          align-items: center;
          gap: 6px;
          color:
            rgba(255,255,255,0.38);
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.7px;
          transition:
            color 0.2s ease;
        }

        .uw-folder-open span {
          color: ${GOLD};
          font-size: 14px;
        }

        .uw-folder-tile:hover
        .uw-folder-open {
          color:
            rgba(255,255,255,0.72);
        }

        /* ======================================================
           SIDEBAR
        ====================================================== */

        .uw-sidebar {
          min-width: 0;
          width: 350px;
        }

        .uw-calendar {
          position: fixed;
          top: 7.5vh;
          right: max(
            clamp(20px, 4vw, 60px),
            calc((100vw - 1700px) / 2)
          );
          width: 350px;
          height: 90vh;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid
            rgba(255,255,255,0.14);
          border-radius: 28px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.10),
              rgba(255,255,255,0.045)
            );
          box-shadow:
            0 25px 70px
              rgba(0,0,0,0.26),
            inset 0 1px 0
              rgba(255,255,255,0.16);
          backdrop-filter:
            blur(25px)
            saturate(135%);
          -webkit-backdrop-filter:
            blur(25px)
            saturate(135%);
        }

        .uw-calendar-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px;
          border-bottom: 1px solid
            rgba(255,255,255,0.09);
        }

        .uw-calendar-header-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border-radius: 14px;
          color: ${GOLD};
          background:
            rgba(235,181,27,0.10);
          border: 1px solid
            rgba(235,181,27,0.20);
        }

        .uw-calendar-title {
          color: ${TEXT};
          font-size: 15px;
          font-weight: 700;
        }

        .uw-calendar-subtitle {
          margin-top: 2px;
          color:
            rgba(255,255,255,0.40);
          font-size: 9px;
        }

        .uw-calendar-body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 17px;
          scrollbar-width: thin;
          scrollbar-color:
            rgba(255,255,255,0.15)
            transparent;
        }

        .uw-calendar-body::-webkit-scrollbar {
          width: 5px;
        }

        .uw-calendar-body::-webkit-scrollbar-thumb {
          background:
            rgba(255,255,255,0.15);
          border-radius: 99px;
        }

        .uw-calendar-month {
          margin-bottom: 21px;
        }

        .uw-calendar-month-title {
          margin-bottom: 10px;
          padding-left: 3px;
          color: ${GOLD};
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }

        .uw-calendar-day {
          margin-bottom: 9px;
        }

        .uw-calendar-day-head {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }

        .uw-calendar-day-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 27px;
          height: 27px;
          border-radius: 9px;
          color:
            rgba(255,255,255,0.90);
          background:
            rgba(255,255,255,0.075);
          border: 1px solid
            rgba(255,255,255,0.10);
          font-size: 10px;
          font-weight: 800;
        }

        .uw-calendar-day-name {
          color:
            rgba(255,255,255,0.35);
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .uw-calendar-events {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .uw-calendar-event {
          display: flex;
          gap: 9px;
          padding: 9px 10px;
          border: 1px solid
            rgba(255,255,255,0.075);
          border-radius: 13px;
          background:
            rgba(255,255,255,0.038);
          transition:
            background 0.2s ease,
            border-color 0.2s ease;
        }

        .uw-calendar-event:hover {
          background:
            rgba(255,255,255,0.07);
          border-color:
            rgba(255,255,255,0.12);
        }

        .uw-calendar-event.is-leave {
          background:
            rgba(235,181,27,0.07);
          border-color:
            rgba(235,181,27,0.18);
        }

        .uw-calendar-event-dot {
          flex: 0 0 auto;
          width: 5px;
          height: 5px;
          margin-top: 5px;
          border-radius: 50%;
          background:
            rgba(255,255,255,0.40);
        }

        .uw-calendar-event.is-leave
        .uw-calendar-event-dot {
          background: ${GOLD};
          box-shadow:
            0 0 8px
              rgba(235,181,27,0.55);
        }

        .uw-calendar-event-content {
          min-width: 0;
          flex: 1;
        }

        .uw-marquee {
          width: 100%;
          overflow: hidden;
          white-space: nowrap;
          color:
            rgba(255,255,255,0.80);
          font-size: 10px;
          font-weight: 650;
        }

        .uw-marquee span {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Titoli stabili: niente marquee in hover, così non c'è più il piccolo tremolio. */
        .uw-marquee:hover span {
          overflow: hidden;
          text-overflow: ellipsis;
          transform: none;
          animation: none;
        }

        .uw-calendar-event-description {
          margin-top: 3px;
          color:
            rgba(255,255,255,0.38);
          font-size: 9px;
          line-height: 1.4;
        }

        .uw-calendar-event-location {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 5px;
          color:
            rgba(255,255,255,0.34);
          font-size: 8px;
        }

        .uw-calendar-empty,
        .uw-calendar-error {
          padding: 30px 10px;
          color:
            rgba(255,255,255,0.40);
          font-size: 11px;
          line-height: 1.6;
          text-align: center;
        }

        .uw-calendar-error {
          color:
            rgba(255,150,150,0.75);
        }

        /* ======================================================
           FOLDER OVERLAY
        ====================================================== */

        .uw-folder-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 25px;
          background:
            rgba(10,11,14,0.58);
          backdrop-filter:
            blur(22px)
            saturate(125%);
          -webkit-backdrop-filter:
            blur(22px)
            saturate(125%);
        }

        .uw-folder-overlay.is-opening {
          animation:
            uw-overlay-in
            0.25s
            ease
            both;
        }

        .uw-folder-overlay.is-closing {
          animation:
            uw-overlay-out
            0.28s
            ease
            both;
        }

        @keyframes uw-overlay-in {
          from {
            opacity: 0;
          }

          to {
            opacity: 1;
          }
        }

        @keyframes uw-overlay-out {
          from {
            opacity: 1;
          }

          to {
            opacity: 0;
          }
        }

        /* ======================================================
           MODAL
        ====================================================== */

        .uw-folder-modal {
          position: relative;
          width: min(
            920px,
            100%
          );
          max-height:
            min(
              760px,
              calc(100vh - 50px)
            );
          overflow: auto;
          padding: 26px;
          border: 1px solid
            rgba(255,255,255,0.17);
          border-radius: 32px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,0.13),
              rgba(255,255,255,0.055)
            );
          box-shadow:
            0 40px 120px
              rgba(0,0,0,0.48),
            inset 0 1px 0
              rgba(255,255,255,0.20),
            inset 0 -1px 0
              rgba(0,0,0,0.12);
          backdrop-filter:
            blur(30px)
            saturate(145%);
          -webkit-backdrop-filter:
            blur(30px)
            saturate(145%);
          animation:
            uw-modal-in
            0.35s
            cubic-bezier(
              .2,
              .8,
              .2,
              1
            )
            both;
        }

        .uw-folder-overlay.is-closing
        .uw-folder-modal {
          animation:
            uw-modal-out
            0.28s
            ease
            both;
        }

        @keyframes uw-modal-in {
          from {
            opacity: 0;
            transform:
              scale(0.90)
              translateY(20px);
          }

          to {
            opacity: 1;
            transform:
              scale(1)
              translateY(0);
          }
        }

        @keyframes uw-modal-out {
          from {
            opacity: 1;
            transform:
              scale(1)
              translateY(0);
          }

          to {
            opacity: 0;
            transform:
              scale(0.94)
              translateY(10px);
          }
        }

        .uw-folder-modal::-webkit-scrollbar {
          width: 5px;
        }

        .uw-folder-modal::-webkit-scrollbar-thumb {
          background:
            rgba(255,255,255,0.14);
          border-radius: 99px;
        }

        .uw-folder-modal-shine {
          position: absolute;
          top: -180px;
          right: -100px;
          width: 350px;
          height: 350px;
          border-radius: 50%;
          background:
            rgba(235,181,27,0.075);
          filter: blur(55px);
          pointer-events: none;
        }

        .uw-folder-modal-header {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 23px;
        }

        .uw-folder-modal-heading {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .uw-folder-modal-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 18px;
          color: ${GOLD};
          background:
            rgba(235,181,27,0.10);
          border: 1px solid
            rgba(235,181,27,0.24);
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.12);
        }

        .uw-folder-modal-kicker {
          margin-bottom: 2px;
          color:
            rgba(255,255,255,0.35);
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .uw-folder-modal-header h2 {
          margin: 0;
          color: ${TEXT};
          font-size: 26px;
          font-weight: 650;
          letter-spacing: -0.8px;
        }

        .uw-folder-modal-header p {
          margin:
            2px
            0
            0;
          color:
            rgba(255,255,255,0.42);
          font-size: 10px;
        }

        .uw-folder-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border: 1px solid
            rgba(255,255,255,0.12);
          border-radius: 13px;
          color:
            rgba(255,255,255,0.58);
          background:
            rgba(255,255,255,0.055);
          cursor: pointer;
          transition:
            all 0.2s ease;
        }

        .uw-folder-close:hover {
          color: #fff;
          background:
            rgba(255,255,255,0.10);
          border-color:
            rgba(255,255,255,0.22);
          transform:
            rotate(4deg);
        }

        /* ======================================================
           MODAL APPS
        ====================================================== */

        .uw-folder-app-grid {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns:
            repeat(
              3,
              minmax(0, 1fr)
            );
          gap: 12px;
        }

        .uw-folder-app {
          position: relative;
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          padding: 13px;
          border: 1px solid
            rgba(255,255,255,0.10);
          border-radius: 18px;
          color: ${TEXT};
          background:
            rgba(255,255,255,0.045);
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.08);
          cursor: pointer;
          text-align: left;
          transition:
            transform 0.22s ease,
            border-color 0.22s ease,
            background 0.22s ease;
        }

        .uw-folder-app:hover {
          transform:
            translateY(-3px);
          border-color:
            rgba(235,181,27,0.26);
          background:
            rgba(235,181,27,0.065);
        }

        .uw-folder-app-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          width: 48px;
          height: 48px;
          overflow: hidden;
          border-radius: 14px;
          color: ${GOLD};
          background:
            rgba(255,255,255,0.065);
          border: 1px solid
            rgba(255,255,255,0.09);
        }

        .uw-folder-app-icon img {
          width: 72%;
          height: 72%;
          object-fit: contain;
        }

        .uw-folder-app-title {
          min-width: 0;
          overflow: hidden;
          color:
            rgba(255,255,255,0.82);
          font-size: 11px;
          font-weight: 650;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .uw-folder-app-arrow {
          margin-left: auto;
          color:
            rgba(255,255,255,0.25);
          font-size: 16px;
          transition:
            color 0.2s ease,
            transform 0.2s ease;
        }

        .uw-folder-app:hover
        .uw-folder-app-arrow {
          color: ${GOLD};
          transform:
            translateX(2px);
        }

        /* ======================================================
           SERVICE CARD
        ====================================================== */

        .uw-service-card {
          overflow: hidden;
          border: 1px solid
            rgba(255,255,255,0.12) !important;
          border-radius: 24px !important;
          color: ${TEXT};
          background:
            rgba(255,255,255,0.06) !important;
          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,0.12);
          backdrop-filter:
            blur(20px);
          -webkit-backdrop-filter:
            blur(20px);
        }

        .uw-service-card-inner {
          display: flex;
          gap: 18px;
          padding: 22px;
        }

        .uw-service-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 62px;
          height: 62px;
          flex: 0 0 auto;
          border-radius: 18px;
          color: ${GOLD};
          background:
            rgba(255,255,255,0.07);
          border: 1px solid
            rgba(255,255,255,0.11);
        }

        .uw-service-icon img {
          width: 70%;
          height: 70%;
          object-fit: contain;
        }

        .uw-service-content h5 {
          margin: 0;
          color: ${TEXT};
          font-size: 16px;
          font-weight: 650;
        }

        .uw-service-content p {
          margin:
            5px
            0
            12px;
          color: ${TEXT_MUTED};
          font-size: 12px;
          line-height: 1.5;
        }

        /* ======================================================
           RESPONSIVE
        ====================================================== */

        @media (max-width: 1200px) {

          .uw-main-layout {
            grid-template-columns:
              minmax(0, 1fr)
              310px;
            gap: 22px;
          }

          .uw-sidebar,
          .uw-calendar {
            width: 310px;
          }

          .uw-folder-row {
            grid-template-columns:
              repeat(3, minmax(0, 1fr));
          }

          .uw-folder-tile {
            min-height: 210px;
            padding: 16px;
          }

          .uw-folder-info {
            left: 16px;
            right: 16px;
            bottom: 14px;
          }

          .uw-folder-open {
            right: 15px;
            bottom: 16px;
          }
        }

        @media (max-width: 992px) {

          .uw-main-layout {
            grid-template-columns: 1fr;
          }

          .uw-sidebar {
            display: none;
          }

          .uw-folder-row {
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
          }

          .uw-folder-app-grid {
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 700px) {

          .uw-home-container {
            padding:
              20px
              15px
              45px;
          }

          .uw-intro {
            padding:
              20px
              3px
              30px;
          }

          .uw-intro h1 {
            font-size: 43px;
            letter-spacing: -2px;
          }

          .uw-intro p {
            font-size: 13px;
          }

          .uw-dashboard-content {
            align-items: flex-start;
            flex-wrap: wrap;
            padding: 20px;
          }

          .uw-dashboard-text {
            width:
              calc(
                100% - 84px
              );
          }

          .uw-dashboard-content
          .uw-outline-btn {
            width: 100%;
            margin-top: 3px;
          }

          .uw-folder-row {
            grid-template-columns: 1fr;
          }

          .uw-folder-tile {
            aspect-ratio: 1 / 0.75;
            min-height: 220px;
          }

          .uw-recent-row {
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
          }

          .uw-folder-modal {
            padding: 20px;
            border-radius: 25px;
          }

          .uw-folder-app-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {

          .uw-home-container {
            padding-left: 12px;
            padding-right: 12px;
          }

          .uw-intro h1 {
            font-size: 38px;
          }

          .uw-dashboard-icon {
            width: 52px;
            height: 52px;
          }

          .uw-dashboard-text {
            width:
              calc(
                100% - 70px
              );
          }

          .uw-dashboard-text h2 {
            font-size: 20px;
          }

          .uw-dashboard-text p {
            font-size: 11px;
          }

          .uw-recent-row {
            grid-template-columns: 1fr;
          }

          .uw-folder-tile {
            min-height: 230px;
          }

          .uw-folder-modal-heading {
            gap: 10px;
          }

          .uw-folder-modal-icon {
            width: 48px;
            height: 48px;
          }

          .uw-folder-modal-header h2 {
            font-size: 22px;
          }
        }

        /* ======================================================
           REDUCED MOTION
        ====================================================== */

        @media (prefers-reduced-motion: reduce) {

          .uw-folder-tile,
          .uw-recent-card,
          .uw-folder-app,
          .uw-outline-btn {
            transition: none !important;
          }

          .uw-marquee:hover span {
            animation: none !important;
          }

        }

        /* ======================================================
   CARTELLA - IPAD STYLE
====================================================== */

.uw-folder-tile {
  position: relative;
  min-width: 0;
  min-height: 300px;
  overflow: hidden;

  padding: 18px;

  border: 1px solid
    rgba(255,255,255,0.14);

  border-radius: 28px;

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.115),
      rgba(255,255,255,0.045)
    );

  box-shadow:
    0 22px 55px
      rgba(0,0,0,0.22),

    inset 0 1px 0
      rgba(255,255,255,0.18),

    inset 0 -1px 0
      rgba(0,0,0,0.08);

  backdrop-filter:
    blur(25px)
    saturate(135%);

  -webkit-backdrop-filter:
    blur(25px)
    saturate(135%);

  transition:
    transform 0.28s ease,
    border-color 0.28s ease,
    box-shadow 0.28s ease;
}

.uw-folder-tile-clickable {
  cursor: pointer;
}

.uw-folder-tile-clickable:hover {
  transform:
    translateY(-5px);

  border-color:
    rgba(235,181,27,0.30);

  box-shadow:
    0 30px 70px
      rgba(0,0,0,0.30),

    0 0 35px
      rgba(235,181,27,0.05),

    inset 0 1px 0
      rgba(255,255,255,0.22);
}


/* LUCE SUPERIORE */

.uw-folder-tile::before {
  content: "";

  position: absolute;

  top: 0;
  left: 10%;

  width: 60%;
  height: 1px;

  background:
    linear-gradient(
      90deg,
      rgba(255,255,255,0.45),
      transparent
    );

  pointer-events: none;
}


/* GLOW */

.uw-folder-shine {
  position: absolute;

  width: 190px;
  height: 190px;

  top: -120px;
  right: -70px;

  border-radius: 50%;

  background:
    rgba(235,181,27,0.08);

  filter: blur(35px);

  pointer-events: none;
}


/* ======================================================
   HEADER
====================================================== */

.uw-folder-top {
  position: relative;
  z-index: 2;

  display: flex;
  align-items: center;
  justify-content: space-between;
}

.uw-folder-main-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 44px;
  height: 44px;

  border-radius: 14px;

  color: #ebb51b;

  background:
    linear-gradient(
      145deg,
      rgba(235,181,27,0.18),
      rgba(235,181,27,0.045)
    );

  border: 1px solid
    rgba(235,181,27,0.24);

  box-shadow:
    inset 0 1px 0
      rgba(255,255,255,0.12);
}

.uw-folder-more {
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 36px;
  height: 28px;

  padding: 0 9px;

  border-radius: 999px;

  color:
    rgba(255,255,255,0.68);

  background:
    rgba(255,255,255,0.075);

  border: 1px solid
    rgba(255,255,255,0.11);

  font-size: 10px;
  font-weight: 800;
}


/* ======================================================
   APP PREVIEW
====================================================== */

.uw-folder-app-preview {
  position: relative;
  z-index: 2;

  display: grid;

  grid-template-columns:
    repeat(2, minmax(0, 1fr));

  gap: 10px;

  margin-top: 18px;
}


/* ======================================================
   APP QUADRATA
====================================================== */

.uw-folder-preview-app {
  min-width: 0;

  padding: 0;

  border: 0;

  background: transparent;

  color: #fff;

  text-align: center;

  cursor: pointer;
}

.uw-folder-preview-icon {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  aspect-ratio: 1 / 1;

  max-height: 90px;

  border-radius: 20px;

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.105),
      rgba(255,255,255,0.045)
    );

  border: 1px solid
    rgba(255,255,255,0.105);

  box-shadow:
    inset 0 1px 0
      rgba(255,255,255,0.13),

    0 8px 18px
      rgba(0,0,0,0.12);

  overflow: hidden;

  transition:
    transform 0.22s ease,
    background 0.22s ease,
    border-color 0.22s ease;
}

.uw-folder-preview-app:hover
.uw-folder-preview-icon {
  transform:
    translateY(-3px)
    scale(1.025);

  background:
    rgba(235,181,27,0.09);

  border-color:
    rgba(235,181,27,0.28);

  box-shadow:
    0 10px 25px
      rgba(0,0,0,0.18),

    0 0 20px
      rgba(235,181,27,0.06);
}

.uw-folder-preview-icon::before {
  content: "";

  position: absolute;

  top: 0;
  left: 10%;

  width: 65%;
  height: 1px;

  background:
    linear-gradient(
      90deg,
      rgba(255,255,255,0.30),
      transparent
    );
}

.uw-folder-preview-icon img {
  width: 62%;
  height: 62%;

  object-fit: contain;

  filter:
    drop-shadow(
      0 7px 12px
      rgba(0,0,0,0.15)
    );
}

.uw-folder-preview-icon svg {
  color: #ebb51b;

  filter:
    drop-shadow(
      0 5px 10px
      rgba(235,181,27,0.15)
    );
}


/* NOME APP */

.uw-folder-preview-app span {
  display: block;

  width: 100%;

  margin-top: 6px;

  overflow: hidden;

  color:
    rgba(255,255,255,0.72);

  font-size: 9px;
  font-weight: 600;

  white-space: nowrap;
  text-overflow: ellipsis;
}


/* ======================================================
   INFO CARTELLA
====================================================== */

.uw-folder-info {
  position: absolute;

  left: 18px;
  bottom: 17px;

  z-index: 3;
}

.uw-folder-title {
  color: #f7f7f8;

  font-size: 16px;
  font-weight: 650;

  letter-spacing: -0.2px;
}

.uw-folder-subtitle {
  margin-top: 2px;

  color:
    rgba(255,255,255,0.42);

  font-size: 9px;
}


/* ======================================================
   APRI
====================================================== */

.uw-folder-open {
  position: absolute;

  right: 18px;
  bottom: 18px;

  display: flex;
  align-items: center;
  gap: 6px;

  color:
    rgba(255,255,255,0.35);

  font-size: 9px;
  font-weight: 700;

  text-transform: uppercase;
  letter-spacing: 0.7px;

  transition:
    color 0.2s ease;
}

.uw-folder-open span {
  color: #ebb51b;
  font-size: 14px;
}

.uw-folder-tile:hover
.uw-folder-open {
  color:
    rgba(255,255,255,0.70);
}


/* ======================================================
   MODAL
====================================================== */

.uw-folder-overlay {
  position: fixed;

  inset: 0;

  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 24px;

  overflow: hidden;

  background:
    rgba(8,9,12,0.62);

  backdrop-filter:
    blur(25px)
    saturate(125%);

  -webkit-backdrop-filter:
    blur(25px)
    saturate(125%);
}


/* ======================================================
   MODAL CARD
====================================================== */

.uw-folder-modal {
  position: relative;

  width: min(
    820px,
    100%
  );

  max-width: 820px;

  max-height:
    calc(100vh - 48px);

  overflow-y: auto;
  overflow-x: hidden;

  padding: 27px;

  border: 1px solid
    rgba(255,255,255,0.17);

  border-radius: 32px;

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.13),
      rgba(255,255,255,0.055)
    );

  box-shadow:
    0 45px 120px
      rgba(0,0,0,0.52),

    inset 0 1px 0
      rgba(255,255,255,0.20),

    inset 0 -1px 0
      rgba(0,0,0,0.12);

  backdrop-filter:
    blur(32px)
    saturate(145%);

  -webkit-backdrop-filter:
    blur(32px)
    saturate(145%);

  scrollbar-width: thin;
  scrollbar-color:
    rgba(255,255,255,0.16)
    transparent;
}


/* SOLO SCROLL VERTICALE */

.uw-folder-modal::-webkit-scrollbar {
  width: 5px;
  height: 0;
}

.uw-folder-modal::-webkit-scrollbar-track {
  background: transparent;
}

.uw-folder-modal::-webkit-scrollbar-thumb {
  background:
    rgba(255,255,255,0.16);

  border-radius: 999px;
}

.uw-folder-modal::-webkit-scrollbar-thumb:hover {
  background:
    rgba(255,255,255,0.25);
}


/* ======================================================
   MODAL HEADER
====================================================== */

.uw-folder-modal-header {
  position: relative;
  z-index: 3;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-bottom: 25px;
}

.uw-folder-modal-heading {
  display: flex;
  align-items: center;

  gap: 14px;

  min-width: 0;
}

.uw-folder-modal-icon {
  flex: 0 0 auto;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 56px;
  height: 56px;

  border-radius: 18px;

  color: #ebb51b;

  background:
    linear-gradient(
      145deg,
      rgba(235,181,27,0.16),
      rgba(235,181,27,0.045)
    );

  border: 1px solid
    rgba(235,181,27,0.25);

  box-shadow:
    inset 0 1px 0
      rgba(255,255,255,0.13);
}

.uw-folder-modal-kicker {
  margin-bottom: 2px;

  color:
    rgba(255,255,255,0.35);

  font-size: 8px;
  font-weight: 800;

  letter-spacing: 1.6px;
  text-transform: uppercase;
}

.uw-folder-modal-header h2 {
  margin: 0;

  color: #f7f7f8;

  font-size: 27px;
  font-weight: 650;

  letter-spacing: -0.8px;
}

.uw-folder-modal-header p {
  margin: 3px 0 0;

  color:
    rgba(255,255,255,0.40);

  font-size: 10px;
}


/* ======================================================
   CLOSE
====================================================== */

.uw-folder-close {
  flex: 0 0 auto;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 42px;
  height: 42px;

  border: 1px solid
    rgba(255,255,255,0.12);

  border-radius: 14px;

  color:
    rgba(255,255,255,0.55);

  background:
    rgba(255,255,255,0.055);

  cursor: pointer;

  transition:
    all 0.22s ease;
}

.uw-folder-close:hover {
  color: #fff;

  background:
    rgba(255,255,255,0.10);

  border-color:
    rgba(255,255,255,0.22);

  transform:
    rotate(4deg)
    scale(1.03);
}


/* ======================================================
   MODAL APP GRID
====================================================== */

.uw-folder-app-grid {
  position: relative;
  z-index: 2;

  display: grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );

  gap: 18px;

  width: 100%;
}


/* ======================================================
   APP IPAD
====================================================== */

.uw-folder-app {
  min-width: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 5px;

  border: 0;

  color: #fff;

  background: transparent;

  cursor: pointer;

  text-align: center;
}


/* QUADRATO */

.uw-folder-app-icon {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  aspect-ratio: 1 / 1;

  max-width: 135px;

  border-radius: 25px;

  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.115),
      rgba(255,255,255,0.045)
    );

  border: 1px solid
    rgba(255,255,255,0.12);

  box-shadow:
    0 12px 28px
      rgba(0,0,0,0.16),

    inset 0 1px 0
      rgba(255,255,255,0.15),

    inset 0 -1px 0
      rgba(0,0,0,0.08);

  overflow: hidden;

  transition:
    transform 0.24s
      cubic-bezier(.2,.8,.2,1),

    border-color 0.24s ease,

    background 0.24s ease,

    box-shadow 0.24s ease;
}


/* RIFLESSO */

.uw-folder-app-icon::before {
  content: "";

  position: absolute;

  top: 0;
  left: 9%;

  width: 70%;
  height: 1px;

  background:
    linear-gradient(
      90deg,
      rgba(255,255,255,0.42),
      transparent
    );
}


/* GLOW INTERNO */

.uw-folder-app-icon::after {
  content: "";

  position: absolute;

  width: 55%;
  height: 55%;

  border-radius: 50%;

  background:
    rgba(235,181,27,0.035);

  filter: blur(20px);

  pointer-events: none;
}


/* IMG */

.uw-folder-app-icon img {
  position: relative;
  z-index: 2;

  width: 62%;
  height: 62%;

  object-fit: contain;

  filter:
    drop-shadow(
      0 9px 15px
      rgba(0,0,0,0.17)
    );
}


/* SVG */

.uw-folder-app-icon svg {
  position: relative;
  z-index: 2;

  color: #ebb51b;

  filter:
    drop-shadow(
      0 5px 12px
      rgba(235,181,27,0.15)
    );
}


/* HOVER APP */

.uw-folder-app:hover
.uw-folder-app-icon {
  transform:
    translateY(-5px)
    scale(1.035);

  border-color:
    rgba(235,181,27,0.32);

  background:
    linear-gradient(
      145deg,
      rgba(235,181,27,0.10),
      rgba(255,255,255,0.055)
    );

  box-shadow:
    0 18px 35px
      rgba(0,0,0,0.22),

    0 0 25px
      rgba(235,181,27,0.06),

    inset 0 1px 0
      rgba(255,255,255,0.20);
}


/* NOME */

.uw-folder-app-name {
  width: 100%;

  margin-top: 9px;

  overflow: hidden;

  color:
    rgba(255,255,255,0.82);

  font-size: 10px;
  font-weight: 650;

  line-height: 1.2;

  white-space: nowrap;
  text-overflow: ellipsis;
}


/* ======================================================
   RESPONSIVE MODAL
====================================================== */

@media (max-width: 800px) {

  .uw-folder-app-grid {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));

    gap: 15px;
  }

  .uw-folder-app-icon {
    max-width: 125px;
  }

}


@media (max-width: 560px) {

  .uw-folder-overlay {
    padding: 12px;
  }

  .uw-folder-modal {
    max-height:
      calc(100vh - 24px);

    padding: 19px;

    border-radius: 25px;
  }

  .uw-folder-app-grid {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));

    gap: 12px;
  }

  .uw-folder-app-icon {
    border-radius: 18px;
  }

  .uw-folder-modal-header h2 {
    font-size: 22px;
  }

  .uw-folder-modal-icon {
    width: 48px;
    height: 48px;
    border-radius: 15px;
  }

}


@media (max-width: 390px) {

  .uw-folder-app-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));

    gap: 15px;
  }

}



/* ======================================================
   APPLE LIBRARY FOLDERS — FINAL OVERRIDES
   ====================================================== */

/* Le cartelle diventano tile pulite: quadrato glass + nome sotto. */
.uw-folder-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

.uw-folder-tile {
  position: relative;
  min-width: 0;
  min-height: 0;
  aspect-ratio: auto;
  overflow: visible;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  cursor: default;
  transition: none;
}

.uw-folder-tile::before,
.uw-folder-shine,
.uw-folder-top,
.uw-folder-open,
.uw-folder-subtitle {
  display: none !important;
}

.uw-folder-tile-clickable {
  cursor: pointer;
}

.uw-folder-tile-clickable:hover {
  transform: none;
  border-color: transparent;
  box-shadow: none;
}

/* Il vero "folder icon" Apple-like. */
.uw-folder-mini-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  aspect-ratio: 1 / 1;
  margin: 0;
  padding: 14px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 30px;
  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.14),
      rgba(255,255,255,0.055)
    );
  box-shadow:
    0 22px 55px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.19),
    inset 0 -1px 0 rgba(0,0,0,0.08);
  backdrop-filter: blur(26px) saturate(140%);
  -webkit-backdrop-filter: blur(26px) saturate(140%);
  transition:
    transform 0.24s cubic-bezier(.2,.8,.2,1),
    border-color 0.24s ease,
    box-shadow 0.24s ease;
}

.uw-folder-mini-grid::before {
  content: "";
  position: absolute;
  top: 0;
  left: 11%;
  width: 58%;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.48),
    transparent
  );
  pointer-events: none;
}

.uw-folder-tile-clickable:hover .uw-folder-mini-grid {
  transform: translateY(-4px) scale(1.012);
  border-color: rgba(235,181,27,0.28);
  box-shadow:
    0 28px 65px rgba(0,0,0,0.28),
    0 0 28px rgba(235,181,27,0.045),
    inset 0 1px 0 rgba(255,255,255,0.22);
}

/* Le 4 app della preview sono quadrate e hanno il nome DENTRO. */
.uw-folder-mini-icon {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  min-width: 0;
  padding: 8px 7px 7px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.11);
  border-radius: 20px;
  color: rgba(255,255,255,0.88);
  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.105),
      rgba(255,255,255,0.045)
    );
  box-shadow:
    0 7px 18px rgba(0,0,0,0.12),
    inset 0 1px 0 rgba(255,255,255,0.12);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
}

.uw-folder-mini-icon:hover {
  transform: scale(1.035);
  color: #fff;
  border-color: rgba(235,181,27,0.30);
  background:
    linear-gradient(
      145deg,
      rgba(235,181,27,0.105),
      rgba(255,255,255,0.055)
    );
}

.uw-folder-mini-artwork {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52%;
  height: 52%;
  min-height: 0;
  margin-top: -2px;
}

.uw-folder-mini-artwork img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 6px 10px rgba(0,0,0,0.16));
}

.uw-folder-mini-artwork svg {
  width: 100%;
  height: 100%;
  max-width: 75px;
  max-height: 75px;
  color: #ebb51b;
  filter: drop-shadow(0 4px 9px rgba(235,181,27,0.14));
}

/* neutralizza la vecchia regola img al 100% sul bottone */
.uw-folder-mini-icon > img {
  width: auto;
  height: auto;
}

.uw-folder-mini-name {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  color: rgba(255,255,255,0.76);
  font-size: 9px;
  font-weight: 650;
  line-height: 1.1;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

/* Nome della cartella sotto al quadrato, come iOS. */
.uw-folder-info {
  position: static;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 7px;
  width: 100%;
  margin-top: 10px;
  padding: 0 4px;
  text-align: center;
}

.uw-folder-title {
  color: rgba(255,255,255,0.90);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.15px;
}

.uw-folder-count {
  color: rgba(255,255,255,0.36);
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
}

/* ======================================================
   MODALE CARTELLA — APPLE-LIKE GRID
   ====================================================== */

.uw-folder-overlay {
  background: rgba(8,9,12,0.54);
  backdrop-filter: blur(28px) saturate(125%);
  -webkit-backdrop-filter: blur(28px) saturate(125%);
}

.uw-folder-modal {
  width: min(760px, 100%);
  max-width: 760px;
  padding: 24px;
  border-radius: 34px;
  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.145),
      rgba(255,255,255,0.065)
    );
}

.uw-folder-modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  margin-bottom: 20px;
}

.uw-folder-modal-heading {
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
}

.uw-folder-modal-icon,
.uw-folder-modal-kicker,
.uw-folder-modal-header p {
  display: none !important;
}

.uw-folder-modal-header h2 {
  margin: 0;
  color: rgba(255,255,255,0.94);
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.45px;
  text-align: center;
}

.uw-folder-close {
  position: absolute;
  top: 0;
  right: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
}

.uw-folder-app-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
}

/* Niente card rettangolari: ogni app è un quadrato. */
.uw-folder-app {
  display: block;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  text-align: center;
  cursor: pointer;
}

.uw-folder-app:hover {
  transform: none;
  border-color: transparent;
  background: transparent;
}

.uw-folder-app-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: none;
  height: auto;
  aspect-ratio: 1 / 1;
  padding: 12px 10px 28px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 25px;
  color: #ebb51b;
  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,0.115),
      rgba(255,255,255,0.047)
    );
  box-shadow:
    0 12px 28px rgba(0,0,0,0.16),
    inset 0 1px 0 rgba(255,255,255,0.15),
    inset 0 -1px 0 rgba(0,0,0,0.08);
  transition:
    transform 0.22s cubic-bezier(.2,.8,.2,1),
    border-color 0.22s ease,
    background 0.22s ease,
    box-shadow 0.22s ease;
}

.uw-folder-app-icon::before {
  content: "";
  position: absolute;
  top: 0;
  left: 9%;
  width: 70%;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.42),
    transparent
  );
}

.uw-folder-app-icon::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 42%;
  background: linear-gradient(
    to top,
    rgba(12,13,16,0.34),
    transparent
  );
  pointer-events: none;
}

.uw-folder-app:hover .uw-folder-app-icon {
  transform: translateY(-4px) scale(1.025);
  border-color: rgba(235,181,27,0.30);
  background:
    linear-gradient(
      145deg,
      rgba(235,181,27,0.095),
      rgba(255,255,255,0.055)
    );
  box-shadow:
    0 18px 35px rgba(0,0,0,0.22),
    0 0 24px rgba(235,181,27,0.05),
    inset 0 1px 0 rgba(255,255,255,0.20);
}

.uw-folder-app-artwork {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  height: 50%;
  margin-top: -5px;
}

.uw-folder-app-artwork img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 8px 14px rgba(0,0,0,0.18));
}

.uw-folder-app-artwork svg {
  width: 100%;
  height: 100%;
  max-width: 34px;
  max-height: 34px;
  color: #ebb51b;
}

/* neutralizza le vecchie regole applicate direttamente alle img */
.uw-folder-app-icon > img,
.uw-folder-app-icon > svg {
  width: auto;
  height: auto;
}

.uw-folder-app-title {
  position: absolute;
  z-index: 3;
  left: 9px;
  right: 9px;
  bottom: 9px;
  min-width: 0;
  overflow: hidden;
  color: rgba(255,255,255,0.88);
  font-size: 10px;
  font-weight: 650;
  line-height: 1.15;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

.uw-folder-app-arrow {
  display: none !important;
}

/* ======================================================
   RESPONSIVE — mantiene il feeling iOS anche su mobile
   ====================================================== */

@media (max-width: 1100px) {
  .uw-folder-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }
}

@media (max-width: 700px) {
  .uw-folder-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .uw-folder-mini-grid {
    gap: 7px;
    padding: 10px;
    border-radius: 24px;
  }

  .uw-folder-mini-icon {
    padding: 6px 5px 5px;
    border-radius: 16px;
  }

  .uw-folder-mini-name {
    font-size: 8px;
  }

  .uw-folder-title {
    font-size: 12px;
  }

  .uw-folder-count {
    display: none;
  }

  .uw-folder-modal {
    padding: 19px;
    border-radius: 27px;
  }

  .uw-folder-app-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .uw-folder-app-icon {
    border-radius: 20px;
  }
}

@media (max-width: 430px) {
  .uw-folder-row {
    gap: 10px;
  }

  .uw-folder-mini-grid {
    gap: 6px;
    padding: 8px;
    border-radius: 21px;
  }

  .uw-folder-mini-icon {
    border-radius: 14px;
  }

  .uw-folder-mini-artwork {
    width: 46%;
    height: 46%;
  }

  .uw-folder-mini-name {
    font-size: 7px;
  }

  .uw-folder-info {
    margin-top: 7px;
  }

  .uw-folder-app-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 13px;
  }

  .uw-folder-app-title {
    font-size: 9px;
  }
}

@media (max-width: 700px) {
  .uw-dashboard-card {
    height: auto !important;
    min-height: 0 !important;
  }

  .uw-dashboard-content {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: start;
    gap: 12px;
    padding: 16px;
  }

  .uw-dashboard-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
  }

  .uw-dashboard-content .uw-outline-btn {
    grid-column: 2;
    justify-self: start;
    width: auto;
  }
}

      `}</style>
    </div>
  );
}

export default Home;